<template>
  <header
    :style="{
      backgroundColor: getWidgetBranding?.data ?? '',
    }"
    :class="[
      'tw-p-5 tw-flex tw-items-center chat-header',
      { 'tw-bg-blue-900': !getWidgetBranding?.data },
    ]"
  >
    <AppButton
      v-if="showBack"
      text
      circle
      data-back
      class="tw-mr-3"
      dark-background
      @click="goBack"
    >
      <AppIcon shape="angle" direction="left" size="16" color="white" />
    </AppButton>

    <div class="tw-flex tw-items-center tw-mr-3">
      <div class="tw-flex-none tw-w-9 tw-h-9 tw-mr-4 tw-relative">
        <img
          class="tw-rounded-full tw-m-auto tw-block tw-h-full tw-w-full tw-object-cover"
          :src="avatar"
          alt="Avatar"
          loading="lazy"
        />
        <span
          v-if="conversationAssignee"
          :class="['avatar-indicator', indicatorColor]"
        ></span>
      </div>

      <AppText
        v-if="!isPending(quickFormStore.quickForm)"
        weight="bold"
        color="white"
      >
        {{ getHeaderTitle }} {{ isHelp ? ` / ${t('pt.help._')}` : '' }}
      </AppText>
    </div>

    <div class="tw-ml-auto" />

    <AppText v-if="isKioskModeEnabled" color="white">{{ t('kiosk') }}</AppText>
    <SocketConnectionIndicator
      v-if="showConnectionIndicator"
      :connected="isChannelsConnected"
      class="tw-flex tw-items-center"
      size="20"
    />

    <SelectLang
      v-if="isHelp"
      @update-teaser-message="
        sendTeaserMessage(
          ref(sessionStorageGet(SessionStorageKeys.Locale)),
          widget
        )
      "
    />

    <router-link
      v-else-if="!isQliqMessageUrl && !isWidgetUnavailableRoute"
      :to="{ name: routeNames.help, query: routeQuery }"
    >
      <AppButton text dark-background circle only-icon>
        <AppIcon shape="help" size="20" color="white" />
      </AppButton>
    </router-link>

    <AppButton
      v-if="isClosable"
      text
      circle
      dark-background
      @click="demoModeStore.updateDemoChatVisibility(false)"
    >
      <AppIcon shape="window-close" size="18" color="white" />
    </AppButton>
  </header>

  <el-alert v-if="isConditionForAlert" type="info">
    {{ complianceMesage }}
  </el-alert>

  <div v-if="aiQuestion" data-ai-question>
    <el-alert :closable="false" type="info" center class="tw-h-[40px]">
      <span v-html="$sanitize(aiQuestion)" />
    </el-alert>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import botIcon from '@shared/assets/images/quincy.png?inline'
import { useUserLocale } from '@shared/utils/composables/locales'
import getLocalizedLabel from '@shared/utils/helpers/localized-label'
import { isPending } from '@shared/utils/helpers/request-status'
import {
  sessionStorageGet,
  SessionStorageKeys,
} from '@shared/utils/helpers/storage/session-storage'

import { ChatWidget } from '@protos/apps/pc/chat_widget_pb'
import { ChatWidgetMessageType } from '@protos/chat_widget_message_type_pb'
import { UserType } from '@protos/user_type_pb'

import agentIcon from '@pc/assets/images/logo-no-bg.svg?inline'
import { useChatSetup } from '@pc/composables/auth-guards'
import { useChatMessages } from '@pc/composables/use-chat-messages'
import {
  useComplianceMessageDisplay,
  useWidgetMessages,
} from '@pc/composables/widgets'
import { getLauncherIcon } from '@pc/helpers/launcher-icon'
import routeNames from '@pc/router/route-names'
import { useChatWidgetsStore } from '@pc/store/chat-widgets'
import { useConversationsStore } from '@pc/store/conversations'
import { useDemoModeStore } from '@pc/store/demo'
import { useQuickFormsStore } from '@pc/store/quick-forms'
import { useSessionStore } from '@pc/store/session'
import { useSocketStore } from '@pc/store/socket'

const SelectLang = defineAsyncComponent(
  () => import('@shared/components/SelectLang.vue')
)
const SocketConnectionIndicator = defineAsyncComponent(
  () => import('@shared/components/SocketConnectionIndicator.vue')
)

const isClosable = window.self !== window.top

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const sessionStore = useSessionStore()
const demoModeStore = useDemoModeStore()
const quickFormStore = useQuickFormsStore()
const chatWidgetsStore = useChatWidgetsStore()
const conversationsStore = useConversationsStore()
const socketStore = useSocketStore()
const { userLocale } = useUserLocale({ isGetUserLocale: true })
const { sendTeaserMessage } = useChatSetup(
  ref(sessionStorageGet(SessionStorageKeys.Locale))
)

const { aiQuestion } = useChatMessages()

const isKioskModeEnabled = computed(() => chatWidgetsStore.isKioskModeEnabled)

const openedForm = computed(() => quickFormStore.quickForm)

const conversation = computed(() => conversationsStore.currentConversation)

const isQuickFormPage = computed(() => route.name === routeNames.quickFormPage)
const isHelp = computed(() => route.name === routeNames.help)
const isTac = computed(() => route.name === routeNames.tac)
const isWidgetUnavailableRoute = computed(
  () =>
    route.name === routeNames.widgetUnavailable ||
    route.name === routeNames.guestChat
)

const isTracker = computed(() => String(route.name).includes('vt'))
const isOneConversationPerPatientOrVisitor = computed(() =>
  Boolean(widget.value?.isOneConversationPerPatientOrVisitor)
)
const isConversation = computed(() => route.name == routeNames.conversation)
const isConditionForAlert = computed(
  () =>
    !isQuickFormPage.value &&
    complianceMesage.value &&
    !isHelp.value &&
    !isTac.value &&
    isComplianceMessageDisplay.value
)

const isTrackerResults = computed(() =>
  String(route.name).includes('vt-result')
)

const conversationAssignee = computed(() => conversation?.value?.assignee)
const widget = computed(() => sessionStore.chatProps?.chatWidget)

const showConversationsList = computed(
  () =>
    !widget.value?.hideOldConversationForPatientVisitor &&
    !isOneConversationPerPatientOrVisitor.value &&
    sessionStore.chatProps?.visitant.case === 'patient' &&
    route.name === routeNames.conversation
)

const routeQuery = computed(() => {
  return 'hash' in route.query || 'appId' in route.query
    ? { hash: route.query.hash, appId: route.query.appId }
    : { ...route.query, appId: sessionStore.chatProps?.appId }
})

const assigneeName = computed(() => {
  const assignee =
    conversationAssignee.value?.upDisplayName ||
    `${conversationAssignee.value?.firstName} ${conversationAssignee.value?.lastName}`

  const widgetName = getLocalizedLabel(
    widget.value?.name || [],
    userLocale.value
  )?.value
  const showName = showAssignee.value ? assignee : widgetName
  return assignee.includes('undefined') ? '' : showName
})

const isQliqMessageUrl = computed(
  () => sessionStore.chatProps?.visitant.case === 'qliqMessageUrl'
)

const getHeaderTitle = computed(() => {
  if (isQuickFormPage.value) {
    return (
      getLocalizedLabel(openedForm.value?.data?.name || [], userLocale.value)
        ?.value || t('pt.quickFormPage.title')
    )
  } else if (sessionStore.chatProps?.visitant.case === 'qliqMessageUrl') {
    return (
      sessionStore.chatProps?.visitant?.value.senderFullName || t('unknown')
    )
  }

  if (isTracker.value) {
    switch (String(route.name)) {
      case routeNames.viewTrackerIntro:
        return t('pt.tracker._')

      case routeNames.viewTrackerForm:
        return t('pt.tracker.log')

      default:
        return ''
    }
  } else {
    return assigneeName.value
  }
})

const showAssignee = computed(() => widget.value?.showAssignee ?? false)
const assigneeType = computed(() => conversationAssignee.value?.type)
const isOnlineAssignee = computed(() => conversationAssignee.value?.isOnline)

const avatar = computed(() => {
  const iconName = assigneeType.value === UserType.BOT ? botIcon : agentIcon

  return showAssignee.value
    ? conversationAssignee.value?.avatar?.thumbnailUrl || iconName
    : getLauncherIcon(new ChatWidget(widget.value))
})

const indicatorColor = computed(() =>
  isOnlineAssignee.value ? 'tw-bg-green-300' : 'tw-bg-red-400'
)

const getWidgetBranding = computed(() => widget.value?.branding)
const showBack = computed(
  () =>
    isHelp.value ||
    isTracker.value ||
    showConversationsList.value ||
    isQuickFormPage.value
)
const { isSocketLoading, isChannelsConnected } = storeToRefs(socketStore)

const showConnectionIndicator = computed(() => {
  return isConversation.value && !isSocketLoading.value
})

const goBack = () => {
  const query = router.currentRoute.value.query ?? {}

  if (showConversationsList.value) {
    router.push({ name: routeNames.conversationsList, query })
    return
  }

  if (isTrackerResults.value) {
    router.push({ name: routeNames.landingPage, query })
    return
  }

  if (isQuickFormPage.value) {
    router.push({
      name: routeNames.conversation,
      params: {
        conversationId: Number(route.params.conversationId),
      },
      query,
    })
    return
  }

  router.back()
}

const complianceMesage = useWidgetMessages(ChatWidgetMessageType.COMPLIANCE)
const isComplianceMessageDisplay = useComplianceMessageDisplay(
  ChatWidgetMessageType.COMPLIANCE
)
</script>

<style lang="scss" scoped>
.chat-header {
  background-color: #143d67;
}

.user-avatar-img {
  width: 40px;
}

.avatar {
  max-width: 50px;

  &-indicator {
    @apply tw-absolute tw-border-blue-900 tw-bottom-[-3px] tw-right-0 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-border-2;
  }
}

:deep(.el-select) {
  @apply tw-text-black tw-text-opacity-90;
}

:deep(.el-input__suffix) {
  @apply tw-hidden;
}

:deep(.el-input__prefix) {
  right: 12px;
  @apply tw-flex tw-items-center tw-text-black tw-text-opacity-60 tw-left-auto;
}
</style>
